"use client";
import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import debounce from "lodash/debounce";

import { GetScreenType, getIsLargeScreen } from "@stadium/helpers/device";
import { mobileScreenType, tabletScreenType, desktopScreenType } from "@stadium/helpers/style";

type CurrentDeviceContextType = {
  currentDevice?: typeof mobileScreenType | typeof tabletScreenType | typeof desktopScreenType;
  isMobileDevice: boolean;
  isTabletDevice: boolean;
  isDesktopDevice: boolean;
  getDeviceSpecificSizes: (mobile: string, desktop: string, tab: string) => string;
  isLargeScreen: boolean;
};

// define intiial values for SSR to work
export const CurrentDeviceContext = React.createContext<Partial<CurrentDeviceContextType>>({
  currentDevice: undefined,
  getDeviceSpecificSizes: () => "",
  isMobileDevice: false,
  isTabletDevice: false,
  isDesktopDevice: true,
  isLargeScreen: false,
});

const subscribeToResize = (onStoreChange: () => void) => {
  const handler = debounce(onStoreChange, 200, { trailing: true });
  window.addEventListener("resize", handler);
  return () => {
    handler.cancel();
    window.removeEventListener("resize", handler);
  };
};

const ResponsiveContent = ({ children, breakpoints, defaultDevice }) => {
  const getSnapshot = useCallback(
    () => GetScreenType({ breakpoints, defaultDevice }),
    [breakpoints, defaultDevice],
  );

  const getServerSnapshot = useCallback(() => defaultDevice || desktopScreenType, [defaultDevice]);

  const currentDevice = useSyncExternalStore(subscribeToResize, getSnapshot, getServerSnapshot);

  const getDeviceSpecificSizes = useCallback(
    (mobile: string, desktop: string, tab: string) => {
      if (currentDevice === mobileScreenType) {
        return mobile;
      }
      return currentDevice === tabletScreenType ? tab : desktop;
    },
    [currentDevice],
  );

  const isLargeScreen = getIsLargeScreen();

  const contextValue = useMemo<CurrentDeviceContextType>(
    () => ({
      currentDevice,
      getDeviceSpecificSizes,
      isMobileDevice: currentDevice === mobileScreenType,
      isTabletDevice: currentDevice === tabletScreenType,
      isDesktopDevice: currentDevice === desktopScreenType,
      isResponsiveDevice: [mobileScreenType, tabletScreenType].includes(currentDevice),
      isLargeScreen,
    }),
    [currentDevice, getDeviceSpecificSizes, isLargeScreen],
  );

  return (
    <CurrentDeviceContext.Provider value={contextValue}>{children}</CurrentDeviceContext.Provider>
  );
};

export default ResponsiveContent;
